import {getButtonOutlineWhite, FilterContrastProvider} from '@hconnect/common/styles'
import {CalendarDatePicker, CalendarDatePickerRange} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import {Box, Button, useMediaQuery, useTheme} from '@mui/material'
import moment from 'moment-timezone'
import {memo, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE, SLA_KPI_MIN_YEAR} from '../consts'
import {
  EditKpisButton,
  EditKpisModalContainer,
  SlaCommentsModalContainer,
  SlaKpiTabsValuesContainer,
  SlaVerticalNavigationContainer
} from '../containers'
import {useSlaTrackingPageState} from '../hooks'
import {SlaTrackingLayout} from '../layouts'

const calendarType = CalendarDatePickerRange.Yearly

type Props = {
  titleSlot?: React.ReactNode
  inverted?: boolean
}

const MemoizedSlaKpiTabsValuesContainer = memo(SlaKpiTabsValuesContainer)
const MemoizedSlaVerticalNavigationContainer = memo(SlaVerticalNavigationContainer)

export const SlaTrackingPage = ({titleSlot, inverted = false}: Props) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const timezone = useUpmPlantTimezone()
  const {
    tabType: selectedTab,
    setTabType: onTabChange,
    year,
    setYear: onYearChange,
    isCommentsModalOpen,
    openCommentsModal,
    closeCommentsModal,
    isEditKpisModalOpen,
    openEditKpisModal,
    closeEditKpisModal,
    editModalTabFilter,
    setEditModalTabFilter,
    editModalSearchTerm,
    setEditModalSearchTerm
  } = useSlaTrackingPageState({timezone})

  // Adding 1 month to the year to align with the calendar picker behavior
  // This is to ensure that the calendar picker shows the year not matter the current timezone.
  // If using only a year it's on the threshold between current and previous year which changes the year based on the timezone.
  // At the end `year` is just a number representing the year so the month will not affect the result
  const selectedDate = useMemo(() => moment(year.toString()).add(1, 'months').toISOString(), [year])
  const minDateAllowed = useMemo(() => moment(SLA_KPI_MIN_YEAR).add(1, 'months'), [])
  const maxDateAllowed = useMemo(
    () => moment(new Date().getFullYear().toString()).add(1, 'months'),
    []
  )

  return (
    <>
      <SlaTrackingLayout
        titleSlot={titleSlot}
        headerActionsSlot={
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" justifyContent="flex-end">
            <FilterContrastProvider isContrastMode={inverted}>
              <EditKpisButton onClick={openEditKpisModal} year={year} />
              <Button
                variant="outlined"
                startIcon={<ChatBubbleOutlineIcon />}
                onClick={openCommentsModal}
                sx={(theme) => getButtonOutlineWhite(theme)}
              >
                {t('commentsModal.title')}
              </Button>
            </FilterContrastProvider>
            <Box maxWidth="200px" marginTop={1}>
              <CalendarDatePicker
                label={t(`calendarDatePickerRange.${calendarType}`)}
                selectedDate={selectedDate}
                contrastingText={!inverted}
                timeZone={timezone}
                setSelectedDate={(date) => {
                  const newValue = date ? moment(date) : moment(new Date().getFullYear().toString())
                  onYearChange(newValue.format('YYYY').toString())
                }}
                type={calendarType}
                maxDateAllowed={maxDateAllowed}
                minDateAllowed={minDateAllowed}
              />
            </Box>
          </Box>
        }
        navigationSlot={
          <MemoizedSlaVerticalNavigationContainer
            selectedTab={selectedTab}
            onTabChange={onTabChange}
            selectedYear={year}
          />
        }
        contentSlot={
          <MemoizedSlaKpiTabsValuesContainer
            tabType={selectedTab}
            year={year}
            isDesktop={isDesktop}
          />
        }
      />
      {isCommentsModalOpen && (
        <SlaCommentsModalContainer year={year} onClose={closeCommentsModal} />
      )}
      {isEditKpisModalOpen && (
        <EditKpisModalContainer
          open={isEditKpisModalOpen}
          onClose={closeEditKpisModal}
          year={year}
          tabFilter={editModalTabFilter}
          onTabFilterChange={setEditModalTabFilter}
          searchTerm={editModalSearchTerm}
          onSearchChange={setEditModalSearchTerm}
        />
      )}
    </>
  )
}
